import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  closestCenter
} from "@dnd-kit/core"
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { type TabsProps } from "antd"
import React, { useState } from "react"

import {
  DragHandle,
  DragHandleContext,
  useDragHandleValue
} from "../../../../../../../../components/DragHandle.tsx"

/** The grab target for a tab, rendered inside its label. */
export function TabDragHandle({ visible = true }: { visible?: boolean }) {
  return <DragHandle label={"Reorder tab"} hidden={!visible} />
}

interface SortableTabNodeProps {
  "data-node-key": string
  reorderAllowed: boolean
  children: React.ReactElement
}

function SortableTabNode({
  reorderAllowed,
  children,
  ...props
}: SortableTabNodeProps) {
  const { setNodeRef, transform, transition, isDragging, ...sortable } =
    useSortable({
      id: props["data-node-key"],
      disabled: !reorderAllowed
    })

  const handleValue = useDragHandleValue(sortable)

  const { style: childStyle } = children.props as {
    style?: React.CSSProperties
  }

  // Only the overlay copy follows the cursor. The tab left behind keeps no
  // transform at all — dnd-kit's transform tracks the pointer for the active
  // item, and AntD's own .ant-tabs-tab transition eases it, which reads as a
  // second, lagging tab chasing the cursor.
  const style: React.CSSProperties = isDragging
    ? // Fully hidden rather than dimmed: the overlay copy already represents
      // the tab, so a ghost left behind reads as a duplicate. The slot itself
      // stays, and shuffles with its siblings to show where the drop will land.
      { ...childStyle, opacity: 0, transform: undefined, transition: "none" }
    : {
        ...childStyle,
        transform: CSS.Translate.toString(transform),
        transition
      }

  return (
    <DragHandleContext.Provider value={handleValue}>
      {React.cloneElement(children, {
        ref: setNodeRef,
        style
      } as React.Attributes)}
    </DragHandleContext.Provider>
  )
}

interface SortableTabItem {
  key: string
  label: React.ReactNode
}

interface UseSortableTabBarOptions {
  items: SortableTabItem[]
  reorderAllowed: boolean
  onReorder: (orderedKeys: string[]) => void
}

/**
 * Turns AntD's tab bar into a horizontally sortable one, mirroring the
 * milestone reordering modal: a DragOverlay copy tracks the cursor 1:1 while
 * the original tab stays in place, dimmed.
 */
export function useSortableTabBar({
  items,
  reorderAllowed,
  onReorder
}: UseSortableTabBarOptions): TabsProps["renderTabBar"] {
  const [activeKey, setActiveKey] = useState<string | null>(null)

  const keys = items.map((item) => item.key)
  const activeItem = items.find((item) => item.key === activeKey)

  const handleDragStart = ({ active }: DragStartEvent) =>
    setActiveKey(String(active.id))

  const handleDragCancel = () => setActiveKey(null)

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveKey(null)
    if (!over || active.id === over.id) return

    const from = keys.indexOf(String(active.id))
    const to = keys.indexOf(String(over.id))
    if (from === -1 || to === -1) return

    onReorder(arrayMove(keys, from, to))
  }

  const renderTabBar: TabsProps["renderTabBar"] = (
    tabBarProps,
    DefaultTabBar
  ) => (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={keys} strategy={horizontalListSortingStrategy}>
        <DefaultTabBar {...tabBarProps}>
          {(node) => (
            <SortableTabNode
              key={node.key}
              data-node-key={String(node.key)}
              reorderAllowed={reorderAllowed}
            >
              {node}
            </SortableTabNode>
          )}
        </DefaultTabBar>
      </SortableContext>
      <DragOverlay>
        {activeItem ? (
          <div className={"ant-tabs-tab ant-tabs-tab-active cursor-grabbing"}>
            <div className={"ant-tabs-tab-btn"}>{activeItem.label}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )

  return renderTabBar
}
